// src/services/customerService.js
import ApiService from './api'
import AppURLs from '../config/urls'
import { CustomerModel } from '../utils/customerModel'

class CustomerService {
  
  // 👥 Get all customers with pagination support
  async getAllCustomers(params = {}) {
    try {
      console.log('👥 Fetching all customers with params:', params)
      
      const response = await ApiService.get(AppURLs.CUSTOMERS.GET_ALL, params)
      console.log('✅ Customers fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customers:', error)
      throw error
    }
  }

  // 👥 Get customer by ID
  async getCustomerById(id) {
    try {
      console.log('👥 Fetching customer by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMERS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Customer fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer:', error)
      throw error
    }
  }

  // 👥 Create new customer
  async createCustomer(customerData) {
    try {
      console.log('👥 Creating new customer with data:', customerData)
      
      // Validate required fields
      this.validateCustomerData(customerData)
      
      const response = await ApiService.post(AppURLs.CUSTOMERS.CREATE, customerData)
      console.log('✅ Customer created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating customer:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate mobile number)
        const conflictError = new Error(error.response.data?.message || 'Customer with this mobile number already exists')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 👥 Update customer using PATCH
  async updateCustomer(id, customerData) {
    try {
      console.log('👥 Updating customer:', { id, customerData })
      
      // Validate partial data for updates
      this.validatePartialCustomerData(customerData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMERS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, customerData)
      console.log('✅ Customer updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating customer:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate mobile number)
        const conflictError = new Error(error.response.data?.message || 'Customer data conflicts with existing record')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Customer not found')
        notFoundError.response = error.response
        throw notFoundError
      }
      
      throw error
    }
  }

  // 👥 Delete customer
  async deleteCustomer(id) {
    try {
      console.log('👥 Deleting customer:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMERS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Customer deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting customer:', error)
      
      if (error.response?.status === 404) {
        const notFoundError = new Error('Customer not found')
        notFoundError.response = error.response
        throw notFoundError
      } else if (error.response?.status === 409) {
        const conflictError = new Error('Cannot delete customer. Customer may have associated orders.')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 👥 Get customers by branch
  async getCustomersByBranch(branchId, params = {}) {
    try {
      console.log('👥 Fetching customers by branch:', branchId)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMERS.BY_BRANCH, { branchId })
      const response = await ApiService.get(endpoint, params)
      console.log('✅ Branch customers fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch customers:', error)
      throw error
    }
  }

  // 👥 Search customers
  async searchCustomers(searchParams) {
    try {
      console.log('👥 Searching customers with params:', searchParams)
      const response = await ApiService.get(AppURLs.CUSTOMERS.SEARCH, searchParams)
      console.log('✅ Customer search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching customers:', error)
      throw error
    }
  }

  // 👥 Get customer by mobile number
  async getCustomerByMobile(mobileNumber) {
    try {
      console.log('👥 Fetching customer by mobile number:', mobileNumber)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMERS.BY_MOBILE, { mobileNumber })
      const response = await ApiService.get(endpoint)
      console.log('✅ Customer by mobile fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer by mobile:', error)
      throw error
    }
  }

  // 👥 Validate mobile number availability
  async validateMobile(mobileNumber, excludeId = null) {
    try {
      console.log('👥 Validating mobile number:', mobileNumber)
      const params = { mobileNumber }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.CUSTOMERS.VALIDATE_MOBILE, params)
      console.log('✅ Mobile validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating mobile number:', error)
      throw error
    }
  }

  // 👥 Validate customer name availability
  async validateName(name, excludeId = null) {
    try {
      console.log('👥 Validating customer name:', name)
      const params = { name }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.CUSTOMERS.VALIDATE_NAME, params)
      console.log('✅ Name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating customer name:', error)
      throw error
    }
  }

  // 👥 Get customer statistics
  async getCustomerStats(branchId = null) {
    try {
      console.log('👥 Fetching customer statistics')
      const params = branchId ? { branchId } : {}
      const response = await ApiService.get(AppURLs.CUSTOMERS.STATS, params)
      console.log('✅ Customer stats fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer stats:', error)
      throw error
    }
  }

  // 👥 Update customer status (active/inactive)
  async updateCustomerStatus(id, isActive) {
    try {
      console.log('👥 Updating customer status:', { id, isActive })
      const response = await this.updateCustomer(id, { isActive })
      console.log('✅ Customer status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating customer status:', error)
      throw error
    }
  }

  // 👥 Bulk delete customers
  async bulkDeleteCustomers(customerIds) {
    try {
      console.log('👥 Bulk deleting customers:', customerIds)
      const response = await ApiService.post(AppURLs.CUSTOMERS.BULK_DELETE, { customerIds })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk delete:', error)
      throw error
    }
  }

  // 👥 Bulk update customer status
  async bulkUpdateStatus(customerIds, isActive) {
    try {
      console.log('👥 Bulk updating customer status:', { customerIds, isActive })
      const response = await ApiService.post(AppURLs.CUSTOMERS.BULK_STATUS, { customerIds, isActive })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk status update:', error)
      throw error
    }
  }

  // 👥 Client-side validation using CustomerModel
  validateCustomerData(customerData) {
    const validation = CustomerModel.validate(customerData)
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0]
      throw new Error(firstError)
    }
  }

  // 👥 Client-side partial validation for updates
  validatePartialCustomerData(customerData) {
    const validation = CustomerModel.validatePartial(customerData)
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0]
      throw new Error(firstError)
    }
  }

  // 👥 Format customer data for API
  formatCustomerForAPI(customerData) {
    const payload = {
      name: customerData.name?.trim(),
      mobileNumber: CustomerModel.formatMobileForAPI(customerData.mobileNumber?.trim()),
      branch: customerData.branch,
      customerType: customerData.customerType || 'normal',
      isActive: customerData.isActive !== false
    }
    
    // Only include dateOfBirth if it's provided
    if (customerData.dateOfBirth && customerData.dateOfBirth.trim() !== '') {
      payload.dateOfBirth = customerData.dateOfBirth
    }
    
    return payload
  }

  // 👥 Get loyalty tier information
  getLoyaltyTier(points) {
    return CustomerModel.getLoyaltyTier(points)
  }

  // 👥 Format customer data for display
  formatCustomerForDisplay(customer) {
    if (!customer) return null
    
    return {
      ...customer,
      formattedMobile: CustomerModel.formatMobileNumber(customer.mobileNumber),
      formattedPointsBalance: CustomerModel.formatPointsBalance(customer.pointsBalance),
      formattedLifetimePointsEarned: CustomerModel.formatLifetimePointsEarned(customer.lifetimePointsEarned),
      formattedLifetimePointsRedeemed: CustomerModel.formatLifetimePointsRedeemed(customer.lifetimePointsRedeemed),
      formattedLoyaltyPoints: CustomerModel.formatLoyaltyPoints(customer.loyaltyPoints || customer.pointsBalance),
      formattedTotalSpend: CustomerModel.formatTotalSpend(customer.totalSpend),
      formattedDateOfBirth: CustomerModel.formatDate(customer.dateOfBirth),
      formattedLastOrderDate: CustomerModel.formatDate(customer.lastOrderDate),
      age: CustomerModel.calculateAge(customer.dateOfBirth),
      loyaltyTier: CustomerModel.getLoyaltyTier(customer.pointsBalance, customer.loyaltyPoints),
      statusBadgeClasses: CustomerModel.getStatusBadgeClasses(customer.isActive)
    }
  }

  // 👥 Filter customers by various criteria
  filterCustomers(customers, filters) {
    if (!Array.isArray(customers)) return []
    
    return customers.filter(customer => {
      // Search by name or mobile
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase()
        const nameMatch = customer.name?.toLowerCase().includes(searchTerm)
        const mobileMatch = customer.mobileNumber?.includes(searchTerm)
        if (!nameMatch && !mobileMatch) return false
      }
      
      // Filter by branch
      if (filters.branchId && customer.branch?._id !== filters.branchId) {
        return false
      }
      
      // Filter by status
      if (filters.status !== undefined && filters.status !== '') {
        const isActive = filters.status === 'active'
        if (customer.isActive !== isActive) return false
      }
      
      // Filter by loyalty tier
      if (filters.loyaltyTier) {
        const tier = CustomerModel.getLoyaltyTier(customer.pointsBalance, customer.loyaltyPoints)
        if (tier.name.toLowerCase() !== filters.loyaltyTier.toLowerCase()) {
          return false
        }
      }
      
      return true
    })
  }
}

export default new CustomerService()